<!--
Fork addition (Stage H): bottom-right action dock — the plan's three
share/save actions as LABELLED buttons floating over the board area's
bottom-right corner (clear of the inspector panel). This replaces the
unlabelled Save / Copy Blueprint / Generate Link icon buttons that
used to sit in the top toolbar:

- Save — opens the same Save / Share dialog the toolbar button did.
- Copy — the Stage D one-click copy: canonical ====Name==== header +
  encoded token, identical to the Save dialog's Default target.
- Share — copies the current page URL (the Stage H anchor keeps it in
  sync with the plan) with "Link copied!" feedback. When the plan is
  too large for a URL and lives in the localStorage fallback instead,
  the URL is useless off this device, so Share copies the blueprint
  string and says so.
-->
<script setup lang="ts">
import {computed, ref, unref} from 'vue';
import {mdiCheck, mdiContentCopy, mdiContentSave, mdiShareVariant} from '@mdi/js';
import {injectGameData} from '@/scripts/data';
import {injectSettings} from '@/scripts/settings';
import {injectBlueprintModel} from '@/scripts/model/store';
import {BlueprintEncoder, FileNameHandler} from '@/scripts/model/serializer';
import {copyTextToClipboard} from '@/scripts/clipboard';
import {useBlueprintAnchorState} from '@/composables';

const gameData = injectGameData();
const settings = injectSettings();
const blueprintModel = injectBlueprintModel();
const {mode: anchorMode} = useBlueprintAnchorState();

const showSaveDialog = ref(false);
const feedback = ref<'copy' | 'share' | undefined>(undefined);
let feedbackTimer: ReturnType<typeof setTimeout> | undefined;

function flashFeedback(kind: 'copy' | 'share') {
    feedback.value = kind;
    if(feedbackTimer)
        clearTimeout(feedbackTimer);
    feedbackTimer = setTimeout(() => {
        feedback.value = undefined;
    }, 2000);
}

//the canonical blueprint string — the same construction the Stage D
//toolbar button used (Save dialog, Default target)
function buildBlueprintString() {
    const encoder = new BlueprintEncoder(gameData, settings);
    const encoded = encoder.encode(blueprintModel.save());
    const handler = new FileNameHandler(settings);
    const blueprintName = blueprintModel.blueprintName || blueprintModel.getDefaultBlueprintName();
    return handler.encodeBlueprintNameHeader(encoder.split(encoded), blueprintName);
}

async function copyBlueprint() {
    if(await copyTextToClipboard(buildBlueprintString()))
        flashFeedback('copy');
}

const isLocalFallback = computed(() => unref(anchorMode) == 'local');
const shareTooltip = computed(() => unref(isLocalFallback)
    ? 'Plan too large for a link — blueprint copied'
    : 'Copy link to this plan');
const shareLabel = computed(() => {
    if(unref(feedback) == 'share')
        return unref(isLocalFallback) ? 'Blueprint copied!' : 'Link copied!';
    return 'Share';
});

async function sharePlan() {
    if(unref(isLocalFallback)) {
        if(await copyTextToClipboard(buildBlueprintString()))
            flashFeedback('share');
    } else if(await copyTextToClipboard(location.href)) {
        flashFeedback('share');
    }
}
</script>

<template>
    <div class="action-dock" @pointerdown.stop>
        <v-tooltip text="Save / share blueprint" location="top">
            <template #activator="{props: tooltipProps}">
                <v-btn
                    v-bind="tooltipProps"
                    size="small"
                    variant="text"
                    :prepend-icon="mdiContentSave"
                    @click="showSaveDialog = true"
                >
                    Save
                </v-btn>
            </template>
        </v-tooltip>
        <v-tooltip text="Copy blueprint text" location="top">
            <template #activator="{props: tooltipProps}">
                <v-btn
                    v-bind="tooltipProps"
                    size="small"
                    variant="text"
                    :prepend-icon="(feedback == 'copy') ? mdiCheck : mdiContentCopy"
                    @click="copyBlueprint()"
                >
                    {{ (feedback == 'copy') ? 'Copied!' : 'Copy' }}
                </v-btn>
            </template>
        </v-tooltip>
        <v-tooltip :text="shareTooltip" location="top">
            <template #activator="{props: tooltipProps}">
                <v-btn
                    v-bind="tooltipProps"
                    size="small"
                    variant="text"
                    :prepend-icon="(feedback == 'share') ? mdiCheck : mdiShareVariant"
                    @click="sharePlan()"
                >
                    {{ shareLabel }}
                </v-btn>
            </template>
        </v-tooltip>
    </div>
    <div class="d-none">
        <save-share-dialog v-model="showSaveDialog" />
    </div>
</template>

<style scoped>
.action-dock {
    position: absolute;
    right: 12px;
    bottom: 12px;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px;
    border-radius: 999px;
    /* theme surface so the pill reads in both light and dark mode */
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.28);
}
/* narrow screens: shrink the chrome, keep the labels */
@media (max-width: 600px) {
    .action-dock {
        right: 8px;
        bottom: 8px;
        padding: 2px;
    }
    .action-dock .v-btn {
        font-size: 11px;
        padding-inline: 8px;
        min-width: 0;
    }
}
</style>
